/**
 * Settings are the parapeters that run webchat and influence its configuration
 * these settings can be loaded from 3 locations:
 * 1- from Link or URL, user is sent to a webchat website with some params, commands, actions to do
 *    URL can contain these in a hash string or query string
 * 2- from cookies, like user identity and info and preferences
 * 3- from Settings API, webchat look and feel and logo can be loaded from remote settings api
 * 4- from JS SDK, the website hosting webchat can use JS SDK to modify how webchat behaves and trigger actions
 */

import storage from './SafeStorage';

const userProperties = ["id", "email", "name", "first_name", "last_name", "full_name", "gender", "timezone", "timezone_offset", "phone_number", "preferred_language", "whatsapp_number", "country", "city", "area", "address_details", "organization", "title", "department"];


export class ProfileWebchatSettings {
  enableUserChooseLanguage?: boolean; //TEMPORARILY, only depend on automation skills to choose language
}

export class WebchatSettings {
  launcherShape?: string;
  enableDefaultColor?: boolean;
  enableAddTitle?: boolean;
  chatTitle?: string;
  bubbleColor?: string;
  chatHeaderColor?: string;
  chatHeaderTxtColor?: string;
  chatHeaderTxt?: string;
  operator?: string;
  operatorImage?: string;
  customerBubbleColor?: string;
  customerBubbleTxtColor?: string;
  agentBubbleColor?: string;
  agentBubbleTxtColor?: string;
  actionsColor?: string;
  sideSpacing?: number;
  bottomSpacing?: number;
  enableShowYourLogo?: boolean;
  logoUrl?: string;
  enableSoundNotification?: boolean;
  enableCustomerRate?: boolean;
  enableGetChatTranscript?: boolean;
  enableWhiteLabel?: boolean;
  whitelistWebsites?: string;
  enableWebviewCaptureLead?: boolean;
  hidePoweredBy?: boolean;
  captureLeadJson?: string;
  enableBlankTarget?: boolean;
  coreAPIBaseUrl?: string;
  enableCustomPoweredBy?: boolean;
  customPoweredByMode?: 'image' | 'text' | 'name';
  poweredByImage?: string;
  poweredByText?: string;
  poweredByLink?: string;
  fontFamilyUrl?: string;
  customCssTheme?: string;
}



export class PreferenceSettings {
  [index: string]: any;
  // Context - related to where we are
  profileId: string | null = null;
  channelId: string | null = null;
  user?: WebchatUser | null = null;
  guid?: string | null = null;
  session?: string | null = null;
  isReturningUser = false;
  token: string | null = null;

  lang = 'ar';
  enableUserChooseLanguage = false;

  // Preferance - related to how webchat behaves
  voiceMode = false;
  continuous = false;
  closeWebchat = true;
  enableNewItemFormat = false;
  getLastTenMessages = false;
  closeButton = true;

  // for handling triggers and actions
  action: string | null = null;  // key for the intent to be tirggered
  actionPayload: any = null;   // Additional Payload data for the intent to be triggered

  currentSession: any = null;
  triggersRules: any = null;

  // any extra data added to the object
  searchParams: any = null;
}


export const WebchatSettingsManager = {

  // loads user from cookie if we visited this before
  // return true if it is a returning user
  loadUser: function (settings: PreferenceSettings): boolean {
    console.log('In loadUser, settings', settings);
    console.log('In loadUser, localStorage guid', storage.get('tactful_guid'));

    if (settings.user) {
      settings.user.timezone_offset = new Date().getTimezoneOffset(); //remove
    }
    if (settings.guid) {
      settings.isReturningUser = true;
    }
    else {
      const tactful_guid = storage.get('tactful_guid');
      const isValidGuidExists = tactful_guid && tactful_guid != 'undefined' && tactful_guid != 'null';
      if (isValidGuidExists) {
        settings.guid = storage.get('tactful_guid');
        settings.isReturningUser = true;
      } else {
        console.log('generate new guid ...');
        settings.guid = this.generate_guid();
        storage.set('tactful_guid', `${settings.guid}`);
      }
    }


    if (settings.voiceMode && !settings.guid?.includes('voice-')) {
      settings.guid = 'voice-' + settings.guid;
    }

    console.log("settings.guid ::::::::::::::::::::::: ", settings.guid);
    return settings.isReturningUser;
  },

  areParamsRequired: function (settings: PreferenceSettings) {
    if (!settings.profileId || settings.profileId === "") {
      return [false, "'profileId' is a required parameter"];
    }
    if (!settings.token || settings.token === "") {
      return [false, "'token' is a required parameter"];
    }
    return [true, ""];
  },

  areParamsValid: function (settings: PreferenceSettings) {
    //check if not allowed content or wrong data type is embed in Tactful.start functions
    let str_attributes = ['profileId', 'session', 'lang', 'token', 'channelId'];                           // guid excluded as its set by us
    for (let attr of str_attributes) {
      if (settings[attr] && !this.validateString(settings[attr])) {
        return [false, "Error with Widget configuration"];
      }
    }
    if (settings.user && !this.validateWebchatUser(settings.user)) {
      return [false, "Error with Widget configuration"];
    }
    return [true, ""];
  },

  validateNumber: function (num: number | any) {
    if (typeof num === "number") {
      return true;
    }
    return false;
  },

  validateString: function (str: string | any) {
    if (typeof str === "string" && !/[`'"|*?~<>^\[\]{}$\n\r?]/.test(str as string)) {
      return true;
    }
    return false;
  },

  validateWebchatUser: function (user: WebchatUser | any) {
    let str_attributes = ['id', 'name', 'first_name', 'last_name', 'full_name', 'gender', 'timezone', "preferred_language", "country", "city", "area", "address_details", "organization", "title", "department"];            // e-mail, phone num, whatsapp number are excluded
    for (let attr of str_attributes) {
      if (user[attr] && !this.validateString(user[attr])) {
        return false;
      }
    }
    if (user.timezone_offset && !this.validateNumber(user.timezone_offset)) { //remove
      return false;
    }
    return true;
  },

  updateSettings: function (settings: PreferenceSettings, newSettings: PreferenceSettings) {
    // copy settings set by the SDK client if they are not undefined
    // WARNING, only loads attributes that we want from the destination setting object
    // so u must first define it in settings above in order to read it.
    Object.assign(settings, newSettings);
    this.parseUrlParams(settings, settings.searchParams);
  },

  splitParams: function (settings: PreferenceSettings, paramsContent: string) {
    if (paramsContent) {
      const params = paramsContent.split('&');
      for (const param of params) {
        const splitedParam = param.split('=');
        if (splitedParam && splitedParam.length == 2) {
          const key: string = splitedParam[0] as any;
          let value: any = splitedParam[1];

          // because "false" is truthy, convert it to boolean
          if (value === "false") {
            value = false;
          }

          // read properties from settings
          if (Object.keys(settings).includes(key)) {
            settings[key] = value;
            // if the property belongs to a user, add to user
          } else if (userProperties.includes(key)) {
            if (!settings.user) {
              settings.user = {};
            }
            settings.user[key] = value;
          } else {
            console.warn("unknown URL param", splitedParam);
          }

        } else {
          console.warn("invalid URL param format", splitedParam);
        }

      }

    }
    return settings;

  },
  parseUrlParams: function (settings: PreferenceSettings, url: URL) {
    if (url && url.hash) {
      this.splitParams(settings, url.hash.split('?')[1]); //to get url params
    }
    if (url && url.search) {
      this.splitParams(settings, url.search.slice(1));
    }
  },

  setCookie: function (cname: string, cvalue: string, exmonths: number) {
    const d = new Date();
    d.setTime(d.getTime() + (exmonths * 30 * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/;SameSite=None;Secure";
  },
  getCookie: function (cname: string) {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  },
  deleteCookie: function (cname: string, _cvalue: string) {
    let cookie = cname + "=;";
    let expires = "expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie = cookie + expires + "path=/;SameSite=None;Secure";
  },
  generate_guid: function () {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
      s4() + '-' + s4() + s4() + s4();
  },

  customize: function (settings: PreferenceSettings) {
    if (settings.profile === 'tactfulkids' || settings.profile === 'covid19') {
      settings.enableNewItemFormat = true; // use video player and carousel
      settings.getLastTenMessages = true;
    }
    if (settings.profile === 'ixchospital') {
      settings.getLastTenMessages = true;
    }

  },
  deleteUser: function (userId: string) {
    this.deleteCookie("tactful_guid", userId);
  }
};

export interface WebchatUser {
  [index: string]: any;
  id?: string;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  gender?: string; //remove
  timezone?: string; //remove
  timezone_offset?: number; //remove
  phone_number?: string;
  preferred_language?: string;
  whatsapp_number?: string;
  country?: string;
  city?: string;
  area?: string;
  address_details?: string;
  organization?: string;
  title?: string;
  department?: string;
}

export interface WebchatEmbedEvent {
  name: 'identifyUser' | 'configure' | 'connect' | 'fullScreen' | 'connected' | 'booted' | 'openIframe' | 'closeIframe' | 'mobileTheme' | 'standaloneMode' |
  'webchatSettings' | 'webchatTheme' | 'unreadCount' | 'event' | 'triggerAction' | 'fireBubbleTrigger' | 'showLastMessage' | 'logout'; // name of the event
  settings?: PreferenceSettings;
  webchatSettings?: WebchatSettings;
  activateFullScreen?: boolean;
  activateMobileTheme?: boolean;
  activateStandaloneMode?: boolean;
  user?: WebchatUser;
  initialized?: boolean;
  closeButton?: boolean;
  unreadCount?: number;
  eventDetails?: EventMessageDetails;
  actionPayload?: any;
  actionName?: string;
  bubbleActive?: boolean;
  lastMessage?: any;
}

export interface ProfileLangs {

  required?: string;
  captureLeadWelcome?: string;
  askName?: string;
  askPhone?: string;
  askEmail?: string;
  confirmInfoLead?: string;
}
export interface EventMessageDetails {
  name: string;
  properties?: { [key: string]: string };
}
